/**
 * The Ask dock: the one @yagni chat, at the bottom of the center.
 *
 * Folded it is a glass bar (the context chip, the composer, ⌘J, send). The
 * composer takes several lines: it grows with what is typed (one to eight
 * rows), Enter sends and Shift+Enter starts a new line, so a note about a
 * plan is never squeezed into one line. Focusing it opens the thread above
 * it as a full-height solid panel over the center, because a chat is read,
 * not glanced through; Escape or the chevron folds it back to the bar. The
 * thread is scoped to the center
 * (`askDockStore.ts`): the workspace unless a surface binds its own (a Team, a
 * ticket, a decision), and each scope keeps its durable thread
 * (`useThreadSession`), so moving around never loses history.
 *
 * The dock reads, explains, and starts tickets. On a ticket it also does what
 * the sheet's buttons do, through the same write path and on the person's
 * word (ADR-0051); the page's external or irreversible buttons (Merge and the
 * rest of the Held list) stay on the page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Message, WsServerMessage } from "@yagni/shared";
import { InlineChat, OpeningMessage } from "../components/InlineChat.js";
import { useThreadSession } from "../hooks/useThreadSession.js";
import { useWebSocket } from "../hooks/useWebSocket.js";
import { onComposerFocusRequest } from "../lib/answerComposer.js";
import { stagePlanAnswerFromRail, type PlanQuestionRef } from "../lib/planAnswerBridge.js";
import { RAIL_USE_AS_ANSWER, RAIL_USED_AS_ANSWER } from "../work/workCopy.js";
import { captureUiError } from "../sentry.js";
import { Btn, cx } from "../queue/primitives.js";
import { ChevronDownIcon, SendIcon } from "../queue/icons.js";
import { Textarea } from "../ui/textarea.js";
import { askDockStore, useAskDock, type AskSeed } from "./askDockStore.js";
import { workScope, type YagniRailScope } from "./yagniRailScopes.js";

/** The Ask bar's composer, for the shell's ⌘J. */
export const ASK_INPUT_SELECTOR = '[data-testid="ask-input"]';

/** How many times a seed waits 50 ms for the thread to go live before it bounces. */
export const SEND_RETRIES = 20;

/** What the bar says when a message it handed off came back unsent. */
export const ASK_NOT_SENT = "Not sent. Press Enter to try again.";

export function focusAskInput(): boolean {
  const input = document.querySelector<HTMLTextAreaElement>(ASK_INPUT_SELECTOR);
  if (!input) return false;
  input.focus();
  return true;
}

export function AskDock() {
  const { open, binding, draft } = useAskDock();
  const scope = binding?.scope ?? workScope();
  const [text, setText] = useState("");
  const [tab, setTab] = useState<"chat" | "comments">("chat");
  const [localSeed, setLocalSeed] = useState<AskSeed | null>(null);
  // Said in the bar when a message bounced back into it, until the reader types or sends.
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Seeds are sent once per id, whichever thread mount sends them.
  const sentSeedIds = useRef(new Set<number>());

  const bindingSeed = binding?.seedRequest ?? null;
  const onBindingSeedSent = binding?.onSeedSent;

  // A new scope always opens on the conversation.
  useEffect(() => {
    setTab("chat");
  }, [scope.key]);

  // A surface handing the dock a question opens it.
  useEffect(() => {
    if (bindingSeed && !sentSeedIds.current.has(bindingSeed.id)) {
      setTab("chat");
      askDockStore.setOpen(true);
    }
  }, [bindingSeed]);

  // A surface's draft ("On COREX-2131, request changes: ") lands in the bar
  // with the caret at its end, for the person to finish and send.
  const draftQuestion = useRef<PlanQuestionRef | null>(null);
  useEffect(() => {
    if (!draft) return;
    setText(draft.text);
    draftQuestion.current = draft.planQuestion ?? null;
    setTab("chat");
    askDockStore.clearDraft();
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(draft.text.length, draft.text.length);
    });
  }, [draft]);

  // "Answer @yagni" on a decision card hands the keyboard to the dock.
  useEffect(
    () =>
      onComposerFocusRequest(() => {
        askDockStore.setOpen(true);
        inputRef.current?.focus();
      }),
    [],
  );

  const seeds = [bindingSeed, localSeed].filter(
    (seed): seed is AskSeed => seed != null && !sentSeedIds.current.has(seed.id),
  );
  const onSeedSent = useCallback(
    (seed: AskSeed) => {
      sentSeedIds.current.add(seed.id);
      setLocalSeed((current) => (current?.id === seed.id ? null : current));
      if (bindingSeed?.id === seed.id) onBindingSeedSent?.();
    },
    [bindingSeed, onBindingSeedSent],
  );

  // A send the thread could not deliver goes back into the bar, so the
  // reader's words are never lost. It is not marked sent: a surface that
  // re-sends the same seed after Try again still gets it through.
  const onSeedFailed = useCallback(
    (seed: AskSeed) => {
      setLocalSeed((current) => (current?.id === seed.id ? null : current));
      setText((current) => current || seed.text);
      setNotice(ASK_NOT_SENT);
      if (bindingSeed?.id === seed.id) onBindingSeedSent?.();
    },
    [bindingSeed, onBindingSeedSent],
  );

  const send = () => {
    const message = text.trim();
    if (!message) return;
    const planQuestion = draftQuestion.current;
    draftQuestion.current = null;
    setNotice(null);
    setLocalSeed({ id: Date.now(), text: message, ...(planQuestion ? { planQuestion } : {}) });
    setText("");
    setTab("chat");
    askDockStore.setOpen(true);
  };

  const fold = () => {
    askDockStore.setOpen(false);
    inputRef.current?.blur();
  };

  const threadKey =
    scope.conversationId !== undefined ? `c:${scope.conversationId ?? "pending"}` : scope.key;

  return (
    <div className={cx("dock glass", open && "open")} data-testid="ask-dock" data-scope={scope.key}>
      {open && (
        <div className="chat">
          <div className="chd">
            <span className="label" data-testid="ask-dock-scope">
              Ask YAGNI · {scope.label}
            </span>
            {binding?.comments && (
              <span className="seg" role="tablist" aria-label="Dock">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "chat"}
                  className={tab === "chat" ? "on" : undefined}
                  onClick={() => setTab("chat")}
                >
                  Chat
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "comments"}
                  className={tab === "comments" ? "on" : undefined}
                  onClick={() => setTab("comments")}
                >
                  Comments
                </button>
              </span>
            )}
            <Btn icon aria-label="Collapse" title="Collapse" onClick={fold}>
              <ChevronDownIcon className="" />
            </Btn>
          </div>
          {tab === "comments" && binding?.comments ? (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {binding.comments}
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {scope.conversationId !== undefined ? (
                <DockThread
                  key={threadKey}
                  conversationId={scope.conversationId}
                  scope={scope}
                  seeds={seeds}
                  onSeedSent={onSeedSent}
                  onSeedFailed={onSeedFailed}
                  onToolResult={binding?.onToolResult}
                  onFill={(fill) => {
                    setText(fill);
                    inputRef.current?.focus();
                  }}
                />
              ) : (
                <ScopedDockThread
                  key={threadKey}
                  scope={scope}
                  seeds={seeds}
                  onSeedSent={onSeedSent}
                  onSeedFailed={onSeedFailed}
                  onToolResult={binding?.onToolResult}
                  onFill={(fill) => {
                    setText(fill);
                    inputRef.current?.focus();
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
      <div className="ask">
        <span className="ctx">{binding?.context ?? ""}</span>
        <Textarea
          ref={inputRef}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setNotice(null);
          }}
          onFocus={() => askDockStore.setOpen(true)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter is a new line; a composing IME keeps Enter.
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              send();
            }
          }}
          placeholder={scope.placeholder}
          aria-label="Ask YAGNI"
          autoComplete="off"
          rows={1}
          autosize
          className="border-0 bg-transparent rounded-none p-0 resize-none focus-visible:ring-0 focus-visible:outline-none"
          data-testid="ask-input"
        />
        {notice && (
          <span className="note" role="status" data-testid="ask-notice">
            {notice}
          </span>
        )}
        <span className="kbd" aria-hidden>
          ⌘J
        </span>
        <button
          type="button"
          className="send"
          aria-label="Send"
          disabled={!text.trim()}
          onClick={send}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

interface ThreadProps {
  scope: YagniRailScope;
  seeds: AskSeed[];
  onSeedSent: (seed: AskSeed) => void;
  onSeedFailed: (seed: AskSeed) => void;
  onToolResult?: (tool: string, result: unknown) => void;
  onFill: (text: string) => void;
}

/** The scope key's durable thread: resolved (resume, else create) when the dock opens. */
function ScopedDockThread(props: ThreadProps) {
  const { conversationId, resolve, error } = useThreadSession(props.scope.key);
  const { seeds, onSeedFailed } = props;
  const scopeKey = props.scope.key;
  useEffect(() => {
    void resolve();
  }, [resolve]);
  // A thread that cannot be opened says so, reports it, and hands any
  // pending message back to the bar.
  useEffect(() => {
    if (!error) return;
    captureUiError(new Error(error), {
      area: "shell",
      operation: "ask_dock_resolve_thread",
      severity: "error",
      expected: false,
      scopeKey,
      conversationId,
    });
    // Reported once per failure, with the scope and thread it failed for.
  }, [error]);
  const pending = seeds[0];
  useEffect(() => {
    if (error && !conversationId && pending) onSeedFailed(pending);
  }, [error, conversationId, pending, onSeedFailed]);
  if (error && !conversationId) {
    return (
      <div className="flex-1 min-h-0 px-5 py-4" role="alert">
        <p className="body-p">This conversation did not open.</p>
        <Btn variant="secondary" size="sm" style={{ marginTop: 8 }} onClick={() => void resolve()}>
          Try again
        </Btn>
      </div>
    );
  }
  return <DockThread {...props} conversationId={conversationId} />;
}

function DockThread({
  conversationId,
  scope,
  seeds,
  onSeedSent,
  onSeedFailed,
  onToolResult,
  onFill,
}: ThreadProps & { conversationId: string | null }) {
  const imperativeRef = useRef<{ sendMessage: (text: string) => void } | null>(null);

  // The plan question this conversation is currently about, and when it was
  // asked: set by a seed that came from one, cleared by any other seed. Only
  // @yagni's replies since then are offered back to it.
  const [answering, setAnswering] = useState<{ question: PlanQuestionRef; since: number } | null>(
    null,
  );
  const [usedReplyIds, setUsedReplyIds] = useState<ReadonlySet<string>>(() => new Set());

  // A seed is sent once. The InlineChat handle appears a render after the
  // conversation resolves, so retry briefly until it is live.
  const seed = seeds[0] ?? null;
  useEffect(() => {
    if (!seed || !conversationId) return;
    let cancelled = false;
    const trySend = (attempt: number) => {
      if (cancelled) return;
      const api = imperativeRef.current;
      if (api) {
        setAnswering(seed.planQuestion ? { question: seed.planQuestion, since: Date.now() } : null);
        api.sendMessage(seed.text);
        onSeedSent(seed);
        return;
      }
      if (attempt < SEND_RETRIES) {
        setTimeout(() => trySend(attempt + 1), 50);
        return;
      }
      captureUiError(new Error("Ask dock could not hand a message to its thread"), {
        area: "shell",
        operation: "ask_dock_send",
        severity: "error",
        expected: false,
        scopeKey: scope.key,
        conversationId,
        attempts: attempt + 1,
      });
      onSeedFailed(seed);
    };
    trySend(0);
    return () => {
      cancelled = true;
    };
  }, [seed, conversationId, scope.key, onSeedSent, onSeedFailed]);

  if (!conversationId) {
    // Before the thread resolves: the same opener bubble the thread shows once
    // it mounts, so nothing shifts on resolve.
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <OpeningMessage text={scope.opening} agentName="@yagni" />
      </div>
    );
  }
  return (
    <>
      {onToolResult && (
        <ToolResultWatcher conversationId={conversationId} onToolResult={onToolResult} />
      )}
      <InlineChat
        conversationId={conversationId}
        agentName="@yagni"
        initialMessages={[]}
        placeholder={scope.placeholder}
        composerVariant="dock"
        composerSuggestions={[...scope.chips]}
        onSuggestionFill={onFill}
        openingMessage={scope.opening}
        imperativeRef={imperativeRef}
        scopeContext={scope.scopeContext}
        {...(answering
          ? {
              renderMessageFooter: (message: Message) => {
                if (!isReplySince(message, answering.since)) return null;
                const used = usedReplyIds.has(message.id);
                return (
                  <div className="msg-use-answer">
                    <button
                      type="button"
                      className="is-quiet"
                      disabled={used}
                      title={answering.question.text}
                      onClick={() => {
                        if (stagePlanAnswerFromRail(answering.question.id, message.content)) {
                          setUsedReplyIds((prev) => new Set(prev).add(message.id));
                        }
                      }}
                    >
                      {used ? RAIL_USED_AS_ANSWER : RAIL_USE_AS_ANSWER}
                    </button>
                  </div>
                );
              },
            }
          : {})}
      />
    </>
  );
}

/**
 * Slack for the server's clock against this one: a reply stamped a moment
 * before the seed left this browser is still the reply to it.
 */
const REPLY_CLOCK_SLACK_MS = 5_000;

/** An @yagni reply that came after the plan question was asked. */
function isReplySince(message: Message, since: number): boolean {
  if (message.senderType !== "agent" || !message.content.trim()) return false;
  const at = Date.parse(message.createdAt);
  return Number.isFinite(at) && at >= since - REPLY_CLOCK_SLACK_MS;
}

/** Follows `tool.result` events for one conversation, so another tab's dock never drives this one. */
function ToolResultWatcher({
  conversationId,
  onToolResult,
}: {
  conversationId: string;
  onToolResult: (tool: string, result: unknown) => void;
}) {
  useWebSocket((msg: WsServerMessage) => {
    if (msg.type !== "tool.result") return;
    if (msg.conversationId !== conversationId) return;
    onToolResult(msg.tool, msg.result);
  });
  return null;
}
